import React, { useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import { ClickAwayListener, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SearchResultOption from "./SearchResultOption";
import useMapSearch from "./useMapSearch";
import {
  useLanguage,
  localizedField,
  t,
} from "../../../contexts/LanguageContext";

const useStyles = makeStyles((theme) => ({
  searchBarWrapper: {
    position: "absolute",
    top: 0,
    right: 0,
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
    zIndex: 101,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    boxSizing: "border-box",
  },
  searchBarExpanded: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "white",
    borderRadius: 28,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    overflow: "hidden",
    animation: "$expandSearchBar 0.3s ease forwards",
  },
  "@keyframes expandSearchBar": {
    "0%": {
      width: 40,
      opacity: 0.5,
    },
    "100%": {
      width: "calc(100vw - 32px)",
      opacity: 1,
    },
  },
  searchInputWrapper: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 28,
      backgroundColor: "transparent",
      "& fieldset": {
        border: "none",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 16px",
    },
  },
}));

function MapSearchPanel({ open, listArray, onSelect, resetKey }) {
  const classes = useStyles();
  const { language } = useLanguage();
  const searchInputRef = useRef(null);
  const {
    searchValue,
    setSearchValue,
    resetVisibleOptionCount,
    resetSearchState,
    handleListboxScroll,
    filterOptions,
  } = useMapSearch(listArray);

  useEffect(() => {
    resetSearchState();
  }, [resetKey, resetSearchState]);

  useEffect(() => {
    if (!open) {
      searchInputRef.current?.blur();
    }
  }, [open]);

  if (!open) return null;

  return (
    <ClickAwayListener
      onClickAway={() => {
        searchInputRef.current?.blur();
      }}
    >
      <div className={classes.searchBarWrapper}>
        <div className={classes.searchBarExpanded}>
          <div className={classes.searchInputWrapper}>
            <Autocomplete
              onSelect={(e) => e.preventDefault()}
              freeSolo
              options={listArray}
              getOptionLabel={(o) =>
                typeof o === "string"
                  ? o
                  : localizedField(o.item ?? o, "target_name", language) ||
                    o.target_name ||
                    ""
              }
              value={searchValue}
              renderOption={(o) => <SearchResultOption option={o} />}
              onChange={(_, value, reason) => {
                const selectItem = value?.item ?? value;
                setSearchValue(selectItem);
                onSelect(selectItem, reason);
              }}
              onInputChange={(_, __, reason) => {
                if (reason === "input" || reason === "clear") {
                  resetVisibleOptionCount();
                }
              }}
              noOptionsText={t("noOptions", language)}
              style={{ width: "100%" }}
              filterOptions={filterOptions}
              ListboxProps={{ onScroll: handleListboxScroll }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputRef={searchInputRef}
                  autoFocus
                  placeholder={t("searchPlaceholder", language)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  InputProps={{
                    ...params.InputProps,
                    type: "text",
                    shrink: false,
                  }}
                  variant="outlined"
                />
              )}
            />
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}

export default React.memo(MapSearchPanel);
